import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { collection, query, where, orderBy, startAfter, limit, getDocs } from "firebase/firestore";
import { db } from "../../firebase/config";
import { Product } from "../../Components/Model/Product";


export const initialFetchProducts = createAsyncThunk(
  "products/initialFetchProducts",
  async (_, { rejectWithValue }) => {
    try {
      const productsRef = collection(db, "products");
      const q = query(productsRef, orderBy("createdAt", "desc"), limit(10));
      const querySnapshot = await getDocs(q);

      const products = querySnapshot.docs.map((doc) =>
        Product.fromMap({ ...doc.data(), productId: doc.id })
      );

      const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
      return { products, lastVisible };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


// Fetch more products for infinite scroll
export const fetchProducts = createAsyncThunk(
  "products/fetchProducts",
  async (_, { getState, rejectWithValue }) => {
    const { filters, lastVisible } = getState().products;

    try {
      const productsRef = collection(db, "products");

      // Initialize base query
      let q = query(productsRef);

      // Apply category filter first
      if (filters.category) {
        q = query(q, where("category", "==", filters.category));
      }

      // Then apply sorting by createdAt
      q = query(q, orderBy("createdAt", "desc"));

      // Add pagination if lastVisible exists
      if (lastVisible) {
        q = query(q, startAfter(lastVisible));
      }

      // Limit results
      q = query(q, limit(10));

      const querySnapshot = await getDocs(q);

      const products = querySnapshot.docs.map((doc) =>
        Product.fromMap({ ...doc.data(), productId: doc.id })
      );

      const newLastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
      return { products, lastVisible: newLastVisible};
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


const productSlice = createSlice({
  name: "products",
  initialState: {
    products: [],
    filters: { category: null },
    lastVisible: null,
    status: "idle",
    error: null,
  },
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.products = [];
      state.lastVisible = null;
      state.status = "idle";
    },
    resetFilters: (state) => {
      state.filters = { category: null };
      state.products = [];
      state.lastVisible = null;
      state.status = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initialFetchProducts.pending, (state) => {
        state.status = "loading";
      })
      .addCase(initialFetchProducts.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.products = action.payload.products;
        state.lastVisible = action.payload.lastVisible;
      })
      .addCase(initialFetchProducts.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(fetchProducts.pending, (state) => {
        state.status = "loading-more";
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        if (action.payload.products.length === 0) {
          state.status = "no-more-products";
        } else {
          state.status = "succeeded";
          state.products = [...state.products, ...action.payload.products];
          state.lastVisible = action.payload.lastVisible;
        }
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});


export const { setFilters, resetFilters } = productSlice.actions;
export default productSlice.reducer;